import { useState, useEffect } from 'react'
import { supabase } from '../config/supabase'
import toast from 'react-hot-toast'

// Mock data for demo mode
const mockCourses = [
  {
    id: 'demo-course-1',
    title: 'Complete Nutrition Fundamentals',
    slug: 'nutrition-fundamentals',
    summary: 'Master the basics of nutrition science with this comprehensive course covering macronutrients, micronutrients, and practical application.',
    price: 49.99,
    cover_image_url: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400&h=300&fit=crop',
    is_published: true,
    created_at: new Date().toISOString()
  },
  {
    id: 'demo-course-2', 
    title: 'Meal Planning Mastery',
    slug: 'meal-planning-mastery',
    summary: 'Learn to create balanced, delicious meal plans that fit your lifestyle and nutritional goals.',
    price: 0,
    cover_image_url: 'https://images.unsplash.com/photo-1547592180-85f173990554?w=400&h=300&fit=crop',
    is_published: true,
    created_at: new Date().toISOString()
  }
]

export const useCourses = (includeUnpublished = false) => {
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isDemo, setIsDemo] = useState(false)

  useEffect(() => {
    fetchCourses()
  }, [includeUnpublished])

  const fetchCourses = async () => {
    try {
      setLoading(true)
      setError(null)

      // Check if courses table exists
      let query = supabase
        .from('courses')
        .select('*')
        .order('created_at', { ascending: false })

      if (!includeUnpublished) {
        query = query.eq('is_published', true)
      }

      const { data, error } = await query

      if (error) {
        console.warn('Courses table not found, using demo data:', error)
        setIsDemo(true)
        setCourses(includeUnpublished ? mockCourses : mockCourses.filter(c => c.is_published))
      } else {
        setIsDemo(false)
        setCourses(data || [])
      }
    } catch (error) {
      console.error('Error fetching courses:', error)
      setError(error.message)
      setIsDemo(true)
      setCourses(includeUnpublished ? mockCourses : mockCourses.filter(c => c.is_published))
    } finally {
      setLoading(false)
    }
  }

  const createCourse = async (courseData) => {
    if (isDemo) {
      toast.error('Database not connected. Course creation not available in demo mode.')
      return { data: null, error: 'Demo mode' }
    }

    try {
      const { data, error } = await supabase
        .from('courses')
        .insert([courseData])
        .select()
        .single()

      if (error) throw error

      setCourses(prev => [data, ...prev])
      toast.success('Course created successfully')
      return { data, error: null }
    } catch (error) {
      console.error('Error creating course:', error)
      toast.error(error.message)
      return { data: null, error }
    }
  }

  const updateCourse = async (id, updates) => {
    if (isDemo) {
      toast.error('Database not connected. Course updates not available in demo mode.')
      return { data: null, error: 'Demo mode' }
    }

    try {
      const { data, error } = await supabase
        .from('courses')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      setCourses(prev => prev.map(course => course.id === id ? data : course))
      toast.success('Course updated successfully')
      return { data, error: null }
    } catch (error) {
      console.error('Error updating course:', error)
      toast.error(error.message)
      return { data: null, error }
    }
  }

  const deleteCourse = async (id) => {
    if (isDemo) {
      toast.error('Database not connected. Course deletion not available in demo mode.')
      return { error: 'Demo mode' }
    }

    try {
      const { error } = await supabase
        .from('courses')
        .delete()
        .eq('id', id)

      if (error) throw error

      setCourses(prev => prev.filter(course => course.id !== id))
      toast.success('Course deleted successfully')
      return { error: null }
    } catch (error) {
      console.error('Error deleting course:', error)
      toast.error(error.message)
      return { error }
    }
  }

  return {
    courses,
    loading,
    error,
    isDemo,
    createCourse,
    updateCourse,
    deleteCourse,
    refetch: fetchCourses
  }
}

export const useCourse = (slug) => {
  const [course, setCourse] = useState(null)
  const [modules, setModules] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (slug) {
      fetchCourse()
    }
  }, [slug])

  const fetchCourse = async () => {
    try {
      setLoading(true)
      setError(null)

      // Try to fetch from database first
      const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .select('*')
        .eq('slug', slug)
        .single()

      if (courseError) {
        // Fallback to mock data
        const mockCourse = mockCourses.find(c => c.slug === slug)
        if (mockCourse) {
          setCourse(mockCourse)
          setModules([]) // No modules in demo
        } else {
          throw new Error('Course not found')
        }
      } else {
        setCourse(courseData)

        // Fetch modules with lessons
        const { data: modulesData, error: modulesError } = await supabase
          .from('modules')
          .select(`
            *,
            lessons (*)
          `)
          .eq('course_id', courseData.id)
          .order('order_index')

        if (modulesError) {
          console.error('Error fetching modules:', modulesError)
          setModules([])
        } else {
          // Sort lessons within each module
          const sortedModules = modulesData.map(module => ({
            ...module,
            lessons: module.lessons?.sort((a, b) => a.order_index - b.order_index) || []
          }))
          setModules(sortedModules)
        }
      }
    } catch (error) {
      console.error('Error fetching course:', error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  return {
    course,
    modules,
    loading,
    error,
    refetch: fetchCourse
  }
}